import { inject } from '@angular/core';
import { CanActivateFn, Router, ActivatedRouteSnapshot } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { ROUTE_PERMISSION_MAP } from '../utils/constants';

/**
 * RBAC Guard - Protege rutas basándose en los permisos específicos del usuario
 * 
 * Niveles de permisos y operaciones HTTP permitidas:
 * - read_X: Solo GET (ver/listar recursos)
 * - run_X: GET + algunos POST (ver/listar + ejecutar/usar el recurso)
 * - write_X: CRUD completo (GET, POST, PUT, DELETE)
 * 
 * Usa lógica de implicaciones: write_X implica read_X, run_X implica read_X
 * Si el usuario no tiene ninguno de los permisos requeridos, redirige a /chatbot
 */
export const rbacGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  // Construir la ruta completa desde la raíz
  const routePath = buildFullPath(route);
  
  // Buscar los permisos requeridos en el mapa
  const requiredPermissions = ROUTE_PERMISSION_MAP[routePath];
  
  // Si no hay mapeo, permitir acceso (ruta pública)
  if (!requiredPermissions) {
    return true;
  }

  // Verificar si el usuario tiene al menos uno de los permisos requeridos
  const hasAccess = authService.hasAnyPermission(requiredPermissions);
  
  if (!hasAccess) {
    console.warn(
      `Acceso denegado a /${routePath}. ` +
      `Permisos requeridos (al menos uno): ${requiredPermissions.join(' | ')}. ` +
      `Roles del usuario: ${authService.getRoles().join(', ') || 'ninguno'}`
    );
    // Redirigir al usuario a la página de selección de chats
    return router.parseUrl('/chatbot/chats');
  }

  return true;
};

/**
 * Construye la ruta completa desde la raíz hasta la ruta actual
 */
function buildFullPath(route: ActivatedRouteSnapshot): string {
  const pathSegments: string[] = [];
  let currentRoute: ActivatedRouteSnapshot | null = route;
  
  // Recorrer desde la ruta actual hacia arriba hasta la raíz
  while (currentRoute) {
    const path = currentRoute.routeConfig?.path;
    if (path && path !== '') {
      pathSegments.unshift(path); // Agregar al inicio del array
    }
    currentRoute = currentRoute.parent;
  }
  
  // Unir los segmentos con '/'
  return pathSegments.join('/');
}
